import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar, Clock, User, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';

interface Shift {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  staffId: string;
  staffName: string;
  status: string;
}

interface StaffMember {
  id: string;
  name: string;
  email: string;
  position: string;
}

interface ShiftSwitchRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (requestData: any) => void;
  myShifts?: Shift[];
  availableShifts?: Shift[];
  staffMembers?: StaffMember[];
}

const ShiftSwitchRequestForm: React.FC<ShiftSwitchRequestFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  myShifts = [],
  availableShifts = [],
  staffMembers = [],
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();

  const [formData, setFormData] = useState({
    originalShiftId: '',
    requestedShiftId: '',
    targetStaffId: '',
    reason: '',
  });

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<
    'select-original' | 'select-requested' | 'select-target' | 'review'
  >('select-original');

  const selectedOriginalShift = myShifts.find(
    (shift) => shift.id === formData.originalShiftId
  );
  const selectedRequestedShift = availableShifts.find(
    (shift) => shift.id === formData.requestedShiftId
  );
  const selectedTargetStaff = staffMembers.find(
    (staff) => staff.id === formData.targetStaffId
  );

  const handleNext = () => {
    if (step === 'select-original' && formData.originalShiftId) {
      setStep('select-requested');
    } else if (step === 'select-requested' && formData.requestedShiftId) {
      setStep('select-target');
    } else if (step === 'select-target' && formData.targetStaffId) {
      setStep('review');
    }
  };

  const handleBack = () => {
    if (step === 'select-requested') {
      setStep('select-original');
    } else if (step === 'select-target') {
      setStep('select-requested');
    } else if (step === 'review') {
      setStep('select-target');
    }
  };

  const handleSubmit = async () => {
    if (!selectedOriginalShift || !selectedRequestedShift) {
      notify(
        translate(
          'pos.staff.shift_switching.form_select_both',
          'Please select both shifts'
        ),
        { type: 'error' }
      );
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        requesterId: selectedOriginalShift.staffId,
        requesterName: selectedOriginalShift.staffName,
        targetStaffId: formData.targetStaffId || null,
        targetStaffName: selectedTargetStaff?.name || null,
        originalShiftId: formData.originalShiftId,
        originalShiftTitle: selectedOriginalShift.title,
        originalShiftDate: selectedOriginalShift.date,
        originalShiftTime: `${selectedOriginalShift.startTime} - ${selectedOriginalShift.endTime}`,
        originalShiftStartTime: new Date(
          `${selectedOriginalShift.date}T${selectedOriginalShift.startTime}`
        ),
        originalShiftEndTime: new Date(
          `${selectedOriginalShift.date}T${selectedOriginalShift.endTime}`
        ),
        requestedShiftId: formData.requestedShiftId,
        requestedShiftTitle: selectedRequestedShift.title,
        requestedShiftDate: selectedRequestedShift.date,
        requestedShiftTime: `${selectedRequestedShift.startTime} - ${selectedRequestedShift.endTime}`,
        requestedShiftStartTime: new Date(
          `${selectedRequestedShift.date}T${selectedRequestedShift.startTime}`
        ),
        requestedShiftEndTime: new Date(
          `${selectedRequestedShift.date}T${selectedRequestedShift.endTime}`
        ),
        status: 'pending',
        reason: formData.reason,
        tenantId: 'current-tenant', // This should come from user context
      };

      await onSubmit(requestData);
      onClose();
    } catch (error) {
      console.error('Error creating shift switch request:', error);
      notify(
        translate(
          'pos.staff.shift_switching.create_error',
          'Error creating shift switch request'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      originalShiftId: '',
      requestedShiftId: '',
      targetStaffId: '',
      reason: '',
    });
    setStep('select-original');
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const renderStepContent = () => {
    switch (step) {
      case 'select-original':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <Calendar className="mx-auto h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-lg font-semibold">
                {translate(
                  'pos.staff.shift_switching.form_select_your_shift',
                  'Select Your Shift'
                )}
              </h3>
              <p className="text-gray-600">
                {translate(
                  'pos.staff.shift_switching.form_select_your_shift_desc',
                  'Choose the shift you want to switch from'
                )}
              </p>
            </div>
            <div className="space-y-2">
              {myShifts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {translate(
                    'pos.staff.shift_switching.form_no_shifts',
                    'No shifts available for switching'
                  )}
                </div>
              ) : (
                myShifts.map((shift) => (
                  <div
                    key={shift.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.originalShiftId === shift.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        originalShiftId: shift.id,
                      }))
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{shift.title}</div>
                        <div className="text-sm text-gray-600">
                          {shift.date} • {shift.startTime} - {shift.endTime}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {shift.status}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'select-requested':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <ArrowLeftRight className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-semibold">
                {translate(
                  'pos.staff.shift_switching.form_select_target_shift',
                  'Select Target Shift'
                )}
              </h3>
              <p className="text-gray-600">
                {translate(
                  'pos.staff.shift_switching.form_select_target_shift_desc',
                  'Choose the shift you want to switch to'
                )}
              </p>
            </div>
            <div className="space-y-2">
              {availableShifts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {translate(
                    'pos.staff.shift_switching.form_no_available',
                    'No available shifts for switching'
                  )}
                </div>
              ) : (
                availableShifts.map((shift) => (
                  <div
                    key={shift.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.requestedShiftId === shift.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        requestedShiftId: shift.id,
                      }))
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{shift.title}</div>
                        <div className="text-sm text-gray-600">
                          {shift.date} • {shift.startTime} - {shift.endTime}
                        </div>
                        <div className="text-sm text-gray-500">
                          {shift.staffName ||
                            translate(
                              'pos.staff.shift_switching.unassigned',
                              'Unassigned'
                            )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {shift.status}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'select-target':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-purple-500 mb-4" />
              <h3 className="text-lg font-semibold">
                {translate(
                  'pos.staff.shift_switching.form_select_target_staff',
                  'Select Target Staff'
                )}
              </h3>
              <p className="text-gray-600">
                {translate(
                  'pos.staff.shift_switching.form_select_target_staff_desc',
                  'Choose who you want to switch with (optional)'
                )}
              </p>
            </div>
            <div className="space-y-2">
              <div
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  formData.targetStaffId === ''
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, targetStaffId: '' }))
                }
              >
                <div className="text-center">
                  <div className="font-medium">
                    {translate(
                      'pos.staff.shift_switching.form_open_request',
                      'Open Request'
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {translate(
                      'pos.staff.shift_switching.form_open_request_desc',
                      'Anyone can claim this shift'
                    )}
                  </div>
                </div>
              </div>
              {staffMembers.map((staff) => (
                <div
                  key={staff.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.targetStaffId === staff.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      targetStaffId: staff.id,
                    }))
                  }
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{staff.name}</div>
                      <div className="text-sm text-gray-600">
                        {staff.position}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">{staff.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <Clock className="mx-auto h-12 w-12 text-orange-500 mb-4" />
              <h3 className="text-lg font-semibold">
                {translate(
                  'pos.staff.shift_switching.form_review',
                  'Review Request'
                )}
              </h3>
              <p className="text-gray-600">
                {translate(
                  'pos.staff.shift_switching.form_review_desc',
                  'Confirm your shift switch request'
                )}
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-2">
                  {translate(
                    'pos.staff.shift_switching.your_shift',
                    'Your Shift'
                  )}
                </h4>
                <div className="text-sm text-blue-700">
                  <div>{selectedOriginalShift?.title}</div>
                  <div>
                    {selectedOriginalShift?.date} •{' '}
                    {selectedOriginalShift?.startTime} -{' '}
                    {selectedOriginalShift?.endTime}
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg bg-green-50">
                <h4 className="font-medium text-green-900 mb-2">
                  {translate(
                    'pos.staff.shift_switching.requested_shift',
                    'Requested Shift'
                  )}
                </h4>
                <div className="text-sm text-green-700">
                  <div>{selectedRequestedShift?.title}</div>
                  <div>
                    {selectedRequestedShift?.date} •{' '}
                    {selectedRequestedShift?.startTime} -{' '}
                    {selectedRequestedShift?.endTime}
                  </div>
                </div>
              </div>

              {selectedTargetStaff && (
                <div className="p-4 border rounded-lg bg-purple-50">
                  <h4 className="font-medium text-purple-900 mb-2">
                    {translate(
                      'pos.staff.shift_switching.form_select_target_staff',
                      'Select Target Staff'
                    )}
                  </h4>
                  <div className="text-sm text-purple-700">
                    <div>{selectedTargetStaff.name}</div>
                    <div>{selectedTargetStaff.position}</div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="reason">
                  {translate(
                    'pos.staff.shift_switching.form_reason_label',
                    'Reason (Optional)'
                  )}
                </Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder={translate(
                    'pos.staff.shift_switching.form_reason_placeholder',
                    'Why do you want to switch shifts?'
                  )}
                  rows={3}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="shift-switch-request-form"
      >
        <DialogHeader>
          <DialogTitle>
            {translate(
              'pos.staff.shift_switching.form_title',
              'Create Shift Switch Request'
            )}
          </DialogTitle>
        </DialogHeader>

        <div
          className="space-y-6"
          style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}
        >
          {/* Progress Steps */}
          <div
            className="flex items-center justify-center space-x-4"
            style={{ pointerEvents: 'auto' }}
          >
            {[
              'select-original',
              'select-requested',
              'select-target',
              'review',
            ].map((stepName, index) => (
              <div
                key={stepName}
                className="flex items-center"
                style={{ pointerEvents: 'auto' }}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step === stepName
                      ? 'bg-blue-500 text-white'
                      : [
                          'select-original',
                          'select-requested',
                          'select-target',
                          'review',
                        ].indexOf(step) > index
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  style={{ pointerEvents: 'auto', cursor: 'default' }}
                >
                  {index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`w-8 h-0.5 ${
                      [
                        'select-original',
                        'select-requested',
                        'select-target',
                        'review',
                      ].indexOf(step) > index
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={step === 'select-original' ? onClose : handleBack}
              disabled={loading}
            >
              {step === 'select-original'
                ? translate('pos.staff.shift_switching.form_cancel', 'Cancel')
                : translate('pos.staff.shift_switching.form_back', 'Back')}
            </Button>

            {step === 'review' ? (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading
                  ? translate(
                      'pos.staff.shift_switching.form_creating',
                      'Creating...'
                    )
                  : translate(
                      'pos.staff.shift_switching.form_create_request',
                      'Create Request'
                    )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={
                  (!formData.originalShiftId && step === 'select-original') ||
                  (!formData.requestedShiftId && step === 'select-requested')
                }
              >
                {translate('pos.staff.shift_switching.form_next', 'Next')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftSwitchRequestForm;
